import React from 'react';
import {Dialog, Form, Button} from 'components/core';
import FilesList from 'components/files/FilesList';
import {Grid, Divider} from '@material-ui/core';
import { Container, Draggable } from "react-smooth-dnd";
import {ProcedureStepsCtxProvider, ProcedureStepsCtx, ProcedureStepFilesCtx, ProcedureStepFilesProvider} from 'components/dataProviders/procedureSteps';
import {makeStyles} from '@material-ui/core/styles';
import {Add, DragHandle} from '@material-ui/icons';
import {Delete} from '@material-ui/icons';
import clsx from 'clsx';
import ARPoint from 'components/procedures/anchors';
import Verifications from 'components/procedures/verifications';
import { ProcedureItemsCtx } from 'components/dataProviders/procedureItems';
import {GetAsFormField} from './templateField';

const styles = makeStyles((theme) => ({
    dialog: {
        maxWidth: '80vw',
        height: '90vh'
    },
    dialogContent:{
        padding: 0
    },
    root: {
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        width: 800
    },
    stepsList: {
        flex: '0 0 200px',
        overflowY: 'auto',
        padding: 10,
        flexShrink: 0,
        '& > *':
        {
            marginBottom: 10
        }
    },
    stepButton: {
        backgroundColor: 'transparent',
        color: theme.palette.text.secondary
    },
    form: {
        flexGrow: 1,
        padding: 25,
        overflowX: 'hidden'
    },
    addButton: {
        backgroundColor: 'transparent',
        color: theme.palette.primary.main
    },
    verificationContainer: {
        width: "100%",
        height: 56,
        display: "flex",
        padding: 10,
        margin: 10,
        backgroundColor: theme.palette.input.background
    },
    verificationType: {
        color: theme.palette.text.secondary,
        float: "left",
        width: "100%",
        fontSize: "12px"
    },
    verificationTitle: {
        color: theme.palette.text.primary,
        float: "left",
        width: "100%",
        overflow: "hidden",
        fontWeight: 500,
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    }
  }), {name: "StepsDialog"});


export default function EditStepDialogWrapper({onConfirm, onChange, fields, loading, procedureID, skipStepContext, ...props})
{
    if(!procedureID) return null;
    const dialog = <EditStepsDialog onConfirm={onConfirm} onChange={onChange} fields={fields} loading={loading} procedureID={procedureID} {...props} />;
    if(skipStepContext) return dialog;
    else return <ProcedureStepsCtxProvider procedureID={procedureID}>
                    {dialog}
            </ProcedureStepsCtxProvider>
}

function EditStepsDialog({onConfirm, onChange, procedureID, ...props})
{
    const classes = styles();
    const [formValues, setFormValues] = React.useState({});
    const ctx = React.useContext(ProcedureStepsCtx);
    const [selectedStep, setSelectedStep] = React.useState(null);

    React.useEffect(function InitializeSelectedStep(){
        if(!selectedStep && ctx.data.array.length > 0) setSelectedStep(ctx.data.array[0].stepID);
        if(selectedStep)
        {
            if(ctx.data.indexed[selectedStep]) setFormValues({...ctx.data.indexed[selectedStep]});
            else if(ctx.data.array.length > 0) setSelectedStep(ctx.data.array[0].stepID);
        }
    }, [ctx, selectedStep]);

    const saveCurrentStep = React.useCallback(async () => {
        if(selectedStep)
        {
            await ctx.funcs.update(selectedStep, formValues);
        }
    }, [selectedStep, ctx, formValues])

    const handleConfirm = React.useCallback(() =>
    {
        saveCurrentStep().then(() => onConfirm ? onConfirm(formValues) : null);
    }, [onConfirm, saveCurrentStep, formValues]);

    function addStep()
    {
        saveCurrentStep().then(() => {
            ctx.funcs.create({name: "Paso "+(ctx.data.array.length+1), description: ""}).then((stepData) => {
                if(stepData) setSelectedStep(stepData.stepID);
            });
        });
    }

    function selectStep(stepID)
    {
        saveCurrentStep().then(() => setSelectedStep(stepID));
    }


    // wrap step form in memo to avoid rerendering when changing saved values
    // it was causing problems because the context provider was changing every render and triggering effects reinitializing values
    const stepComps = React.useMemo(() => {

        function handleFormChange(newValues)
        {
            Object.keys(newValues).forEach((key) => {
                if(key.startsWith("templateField."))
                {
                  if(!newValues.templateFields) newValues.templateFields = [];
        
                  let fieldID = parseInt(key.split(".")[1]);
                  let assigned = false;
                  newValues.templateFields.forEach((field) => {
                    if(field.fieldID === fieldID)
                    {
                      field.value = newValues[key];
                      assigned = true;
                    }
                  });
                  if(!assigned) newValues.templateFields.push({fieldID: fieldID, value: newValues[key]});
        
                  delete newValues[key];
                }
              });
            setFormValues(newValues);
            if(onChange) onChange(newValues);
        }
        if(!selectedStep) return null;
        return <ProcedureStepFilesProvider procedureID={procedureID} stepID={selectedStep}>
            <StepForm stepID={selectedStep} loading={ctx.loading} onChange={(newValues) => handleFormChange(newValues)}/>
        </ProcedureStepFilesProvider>
    }, [selectedStep, ctx, procedureID, onChange]);

    const onDrop = ({ removedIndex, addedIndex }) => {
        //setItems(items => arrayMove(items, removedIndex, addedIndex));
        ctx.funcs.update(ctx.data.array[removedIndex].stepID, {order: addedIndex+1});
      };

    return <Dialog {...props}
        className={classes.dialog}
        title="Añadir pasos"
        subtitle="Añade todos los pasos necesarios para llevar este procedimiento a cabo"
        loading={ctx.loading}
        onConfirm={handleConfirm}
        open={props.open}
        onClose={props.handleClose}
        confirm="Completar"
        cancel="Anterior"
        headerDivider
        contentClass={classes.dialogContent}
        >
        <div className={classes.root}>
            <div className={classes.stepsList}>
            <Container dragHandleSelector=".drag-handle" lockAxis="y" onDrop={onDrop}>
                {ctx.data.array.map((step, index) => <Draggable key={index}>
                    <Button key={index} onClick={()=>selectStep(step.stepID)} className={step.stepID !== selectedStep && classes.stepButton} color="secondary" startIcon={<DragHandle className="drag-handle" />}>Paso {index+1}</Button>
                </Draggable>)}
            </Container>
                <Button className={classes.addButton} color="secondary" onClick={addStep}><Add />Añadir paso</Button>
            </div>
            <Divider orientation="vertical" />
            <div className={classes.form}>
                {/* <StepFormWrapper procedureID={procedureID} stepID={selectedStep} loading={ctx.loading} onChange={(newValues) => handleFormChange(newValues)}/> */}
                {stepComps}
            </div>
        </div>
    </Dialog>
}

function StepForm({stepID, onChange=()=>{}, ...props})
{
    
    const filesCtx = React.useContext(ProcedureStepFilesCtx);
    const stepsCtx = React.useContext(ProcedureStepsCtx);
    const itemsCtx = React.useContext(ProcedureItemsCtx);
    const [items, setItems] = React.useState([]);
    const [values, setValues] = React.useState({name: '', description: '', items: [], order: 1, verifications: []});

    const step = stepsCtx.data.indexed[stepID];

    React.useEffect(function initializeItems() {
        let items = [];
        itemsCtx.items.array.forEach((item) => items.push({value: item.itemID, content: item.name}));
    
        setItems(items);
    }, [itemsCtx]);

    React.useEffect(function InitializeValues() {
        if(!step) return;
        let templateValues = {};
        step.templateFields.forEach((field) => {
            templateValues["templateField."+field.fieldID] = field.value;
          });

        setValues({name: step.name, description: step.description, items: step.items, order: step.order, verifications: step.verifications, ...templateValues});
    }, [step]);

    const uploadFile = React.useCallback(() =>
    {
        filesCtx.funcs.upload();
    }, [filesCtx]);

    const handleFormChange = (newValues) => {
        let tmpValues = {...values, ...newValues};
        setValues(tmpValues);
        onChange(newValues);
    }

    const deleteStep = () => {
        stepsCtx.funcs.delete(stepID);
    }

    function AddAnchor(anchorID)
    {
        let tmpStep = {...step};
        tmpStep.anchorID = anchorID;
        stepsCtx.funcs.update(stepID, tmpStep);
    }

    function RemoveAnchor()
    {
        let tmpStep = {...step};
        tmpStep.anchorID = null;
        stepsCtx.funcs.update(stepID, tmpStep);
    }

    function UpdateVerifications(verifications)
    {
        handleFormChange({...values, verifications: verifications});
    }

    
    if(!step) return null;
    
    const templateFields = step.templateFields.map((field) => GetAsFormField(field));

    // const templateFields = step.templateFields.map((field) => {
    //     return {
    //       type: "text", // TODO: Añadir todos los tipos usando datatype
    //       label: field.name,
    //       value: field.value,
    //       placeholder: field.placeholder,
    //       mandatory: false,
    //       id: "templateField."+field.fieldID
    //     }
    //   });

    const fields = [
        {
            type: "text",
            label: "Nombre",
            id: "name",
            placeholder: "Nombre del procedimiento",
            mandatory: true,
            value: step.name
        },
        {
            type: "textarea",
            label: "Descripción",
            id: "description",
            placeholder: "",
            mandatory: false,
            value: step.description
        },
        ...templateFields,
        {
            type: "custom",
            id: "anchor",
            label: "Realidad Aumentada",
            content: <><ARPoint editable anchorID={step.anchorID} onAdd={AddAnchor} onRemove={RemoveAnchor} /></>
        },
        {
            type: "tagSelector",
            label: "Añadir items",
            id: "items",
            value: step.items,
            items: items,
            placeholder: "Sin items",
            },
        {
            type: "custom",
            id: "files",
            label: "Archivos",
            content: <><FilesList context={ProcedureStepFilesCtx} editable/><Button fullWidth={false} color="primary" onClick={()=>uploadFile()}><Add />Añadir Archivo</Button></>
        },
        {
            type: "custom",
            id: "verifications",
            label: "Informe de tarea",
            content: <><Verifications value={values.verifications} onChange={(verifications)=>UpdateVerifications(verifications)}></Verifications></>
        },
        {
            type: "custom",
            id: "delete",
            content: <><Button fullWidth={false} color="error" onClick={()=>deleteStep()}><Delete />Eliminar paso</Button></>
        }
    ];
    return <Form onChange={(newValues) => handleFormChange(newValues)} fieldErrors={[]} fields={fields} values={values}/>
}